import React, { useState, useMemo } from 'react'
import { Button } from './Button'
import { Card, CardContent, CardHeader, CardTitle } from './Card'
import { BarChart3, LineChart, PieChart, TrendingUp, Download } from 'lucide-react'

interface ChartData {
  label: string
  value: number
  color?: string
}

interface ChartBuilderProps {
  data: ChartData[]
  title?: string
  onExport?: (chartConfig: any) => void
}

type ChartType = 'bar' | 'line' | 'pie'

export function ChartBuilder({ data, title = "Chart", onExport }: ChartBuilderProps) {
  const [chartType, setChartType] = useState<ChartType>('bar')
  const [showValues, setShowValues] = useState(true)
  const [colorScheme, setColorScheme] = useState('blue')

  const colorSchemes = {
    blue: ['#3B82F6', '#60A5FA', '#93C5FD', '#DBEAFE', '#EFF6FF'],
    green: ['#10B981', '#34D399', '#6EE7B7', '#A7F3D0', '#D1FAE5'],
    purple: ['#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE', '#EDE9FE'],
    orange: ['#F59E0B', '#FBBF24', '#FCD34D', '#FDE68A', '#FEF3C7']
  }

  const chartData = useMemo(() => {
    const colors = colorSchemes[colorScheme as keyof typeof colorSchemes]
    return data.map((item, index) => ({
      ...item,
      color: item.color || colors[index % colors.length]
    }))
  }, [data, colorScheme])

  const maxValue = Math.max(...chartData.map(d => d.value))

  const renderBarChart = () => (
    <div className="space-y-3">
      {chartData.map((item, index) => (
        <div key={index} className="flex items-center gap-3">
          <div className="w-20 text-sm text-right text-muted-foreground truncate">
            {item.label}
          </div>
          <div className="flex-1 relative">
            <div
              className="h-8 rounded transition-all duration-500 ease-out"
              style={{
                backgroundColor: item.color,
                width: `${(item.value / maxValue) * 100}%`,
                minWidth: '8px'
              }}
            />
            {showValues && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs font-medium text-white">
                {item.value.toLocaleString()}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )

  const renderLineChart = () => {
    const chartHeight = 200
    const chartWidth = 400
    const padding = 40

    const points = chartData.map((item, index) => {
      const x = padding + (index / (chartData.length - 1)) * (chartWidth - 2 * padding)
      const y = chartHeight - padding - ((item.value / maxValue) * (chartHeight - 2 * padding))
      return { x, y, ...item }
    })

    const pathData = points.map((point, index) => 
      `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
    ).join(' ')

    return (
      <div className="flex justify-center">
        <svg width={chartWidth} height={chartHeight} className="border rounded">
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
            <line
              key={ratio}
              x1={padding}
              y1={chartHeight - padding - (ratio * (chartHeight - 2 * padding))}
              x2={chartWidth - padding}
              y2={chartHeight - padding - (ratio * (chartHeight - 2 * padding))}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          ))}
          
          {/* Line */}
          <path
            d={pathData}
            fill="none"
            stroke={colorSchemes[colorScheme as keyof typeof colorSchemes][0]}
            strokeWidth="3"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          
          {/* Points */}
          {points.map((point, index) => (
            <g key={index}>
              <circle
                cx={point.x}
                cy={point.y}
                r="4"
                fill={point.color}
                stroke="white"
                strokeWidth="2"
              />
              {showValues && (
                <text
                  x={point.x}
                  y={point.y - 10}
                  textAnchor="middle"
                  className="text-xs fill-current text-muted-foreground"
                >
                  {point.value}
                </text>
              )}
            </g>
          ))}
          
          {/* Labels */}
          {points.map((point, index) => (
            <text
              key={index}
              x={point.x}
              y={chartHeight - 10}
              textAnchor="middle"
              className="text-xs fill-current text-muted-foreground"
            >
              {point.label.length > 8 ? point.label.substring(0, 8) + '...' : point.label}
            </text>
          ))}
        </svg>
      </div>
    )
  }

  const renderPieChart = () => {
    const total = chartData.reduce((sum, item) => sum + item.value, 0)
    const radius = 80
    const centerX = 120
    const centerY = 120
    
    let cumulativePercentage = 0
    
    const slices = chartData.map((item) => {
      const percentage = item.value / total
      const startAngle = cumulativePercentage * 2 * Math.PI - Math.PI / 2
      const endAngle = (cumulativePercentage + percentage) * 2 * Math.PI - Math.PI / 2
      
      const largeArcFlag = percentage > 0.5 ? 1 : 0
      const x1 = centerX + radius * Math.cos(startAngle)
      const y1 = centerY + radius * Math.sin(startAngle)
      const x2 = centerX + radius * Math.cos(endAngle)
      const y2 = centerY + radius * Math.sin(endAngle)
      
      const pathData = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ')
      
      cumulativePercentage += percentage
      
      return {
        ...item,
        pathData,
        percentage: percentage * 100,
        startAngle,
        endAngle
      }
    })

    return (
      <div className="flex items-center gap-8">
        <svg width="240" height="240" className="flex-shrink-0">
          {slices.map((slice, index) => (
            <path
              key={index}
              d={slice.pathData}
              fill={slice.color}
              stroke="white"
              strokeWidth="2"
            />
          ))}
        </svg>
        
        <div className="space-y-2">
          {slices.map((slice, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: slice.color }}
              />
              <span className="flex-1">{slice.label}</span>
              <span className="text-muted-foreground">
                {slice.percentage.toFixed(1)}%
              </span>
              {showValues && (
                <span className="font-medium">
                  {slice.value.toLocaleString()}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return renderBarChart()
      case 'line':
        return renderLineChart()
      case 'pie':
        return renderPieChart()
      default:
        return renderBarChart()
    }
  }

  const handleExport = () => {
    const chartConfig = {
      type: chartType,
      data: chartData,
      title,
      showValues,
      colorScheme
    }
    onExport?.(chartConfig)
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {title}
          </CardTitle>
          <div className="flex items-center gap-2">
            {onExport && (
              <Button onClick={handleExport} size="sm" variant="outline" className="gap-1">
                <Download className="w-4 h-4" />
                Export
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Chart Type Selection */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Chart Type:</span>
          <div className="flex gap-1">
            <Button
              onClick={() => setChartType('bar')}
              size="sm"
              variant={chartType === 'bar' ? 'default' : 'outline'}
              className="gap-1"
            >
              <BarChart3 className="w-4 h-4" />
              Bar
            </Button>
            <Button
              onClick={() => setChartType('line')}
              size="sm"
              variant={chartType === 'line' ? 'default' : 'outline'}
              className="gap-1"
            >
              <LineChart className="w-4 h-4" />
              Line
            </Button>
            <Button
              onClick={() => setChartType('pie')}
              size="sm"
              variant={chartType === 'pie' ? 'default' : 'outline'}
              className="gap-1"
            >
              <PieChart className="w-4 h-4" />
              Pie
            </Button>
          </div>
        </div>

        {/* Options */}
        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showValues}
              onChange={(e) => setShowValues(e.target.checked)}
              className="rounded"
            />
            Show Values
          </label>
          
          <div className="flex items-center gap-2">
            <span>Color:</span>
            <select
              value={colorScheme}
              onChange={(e) => setColorScheme(e.target.value)}
              className="px-2 py-1 border rounded text-sm"
            >
              <option value="blue">Blue</option>
              <option value="green">Green</option>
              <option value="purple">Purple</option>
              <option value="orange">Orange</option>
            </select>
          </div>
        </div>

        {/* Chart */}
        <div className="py-4">
          {chartData.length > 0 ? (
            renderChart()
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No data to display</p>
            </div>
          )}
        </div>

        {/* Insights */}
        {chartData.length > 0 && (
          <div className="border-t pt-4">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              Quick Insights
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Total</div>
                <div className="font-medium">
                  {chartData.reduce((sum, item) => sum + item.value, 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Average</div>
                <div className="font-medium">
                  {Math.round(chartData.reduce((sum, item) => sum + item.value, 0) / chartData.length).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Highest</div>
                <div className="font-medium">
                  {Math.max(...chartData.map(d => d.value)).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Data Points</div>
                <div className="font-medium">{chartData.length}</div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}